import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { API_URL } from '../config/API_URLS.tsx';
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import SuccessModal from './SuccessModal.tsx';

function EditUser() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);  
  const documentByParams = queryParams.get('document');
  const rollByParams = parseInt(queryParams.get('role'));
  const [roleName, setRoleName] = useState('');
  const [userData, setUserData] = useState({
    id: null,
    name: '',
    last_name: '',
    document: documentByParams,
    email: '',
    ficha: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        // Seleccionar el endpoint según el tipo de usuario
        let endpoint = '';
        switch(rollByParams) {
          case 1:
            endpoint = `${API_URL}/v1/admin-by-id/${documentByParams}`;
            break;
          case 2:
            endpoint = `${API_URL}/v1/aprendices/id/document/${documentByParams}`;
            break;
          case 3:
            endpoint = `${API_URL}/v1/vigilante-by-id/${documentByParams}`;
            break;
          default:
            endpoint = `${API_URL}/v1/aprendices/id/document/${documentByParams}`;
        }
        
        const response = await authenticatedFetch(endpoint, {
          method: 'get',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Error al cargar los datos del usuario');
        }
        
        const data = await response.json();
        setUserData({
          id: data.id || null,
          name: data.name || '',
          last_name: data.last_name || '',
          document: data.document || '',
          email: data.email || '',
          ficha: data.ficha || ''
        });
        handleRoleName(rollByParams);
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    if (documentByParams && rollByParams) {
      fetchUserData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentByParams, rollByParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleRoleName = () => {
    switch(rollByParams) {
      case 1:
        setRoleName('admins');
        break;
      case 2:
        setRoleName('aprendices');
        break;
      case 3:
        setRoleName('vigilantes');
        break;
      default:
        setRoleName('Desconocido');
    }
  }
  

  // const handlerDelete = async () => { 
  //   try {    
  //     const response = await authenticatedFetch(`${API_URL}/v1/users/delete/${userData.document}/${rollByParams}`, {
  //         method: 'delete',
  //         headers: {
  //             'Content-Type': 'application/json'
  //         }
  //     });
  //     if (!response.ok) {      
  //       throw new Error('Error en la solicitud');
  //     }
  //     // Redirigir después de eliminar exitosamente
  //     alert('Usuario eliminado con éxito');
  //     navigate('/users-administration-list');

  //   } catch (error) {
  //     console.error('Error:', error);
  //     alert('Error al eliminar usuario');
  //   }
  // }

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      let apiURL = "";
      let bodyPre = {};

      if (rollByParams === 1) {
        apiURL = `${API_URL}/v1/admins-update/${documentByParams}`;
        bodyPre = JSON.stringify({ 
          name: userData.name, 
          last_name: userData.last_name, 
          document: userData.document, 
          "registry_date": "string"
        });
      } else if (rollByParams === 3) {
        apiURL = `${API_URL}/v1/vigilantes-update/${documentByParams}`;
        bodyPre = JSON.stringify({ 
          name: userData.name, 
          last_name: userData.last_name, 
          document: userData.document, 
          "registry_date": "string"
        });
      } else if (rollByParams === 2) {
        apiURL = `${API_URL}/v1/aprendiz-update/${documentByParams}`;
        bodyPre = JSON.stringify({ 
          id: userData.id,
          name: userData.name, 
          last_name: userData.last_name, 
          document: parseInt(userData.document), 
          email: userData.email, 
          ficha: parseInt(userData.ficha),
          "registry_date": "string"
        });
      }

      const response = await authenticatedFetch(apiURL, {
        method: 'put',
        body: bodyPre,
        headers: {
          'Content-Type': 'application/json'
        }                
      });

      if (!response.ok) {
        throw new Error('Error en la solicitud');            
      }
      
      await response.json();
      // Mostrar modal de éxito en lugar de alert
      setSuccessMessage('Datos actualizados con éxito');
      setIsSuccessModalOpen(true);
      // La navegación se hará cuando se cierre el modal
    } catch (error) {
      console.error('Error:', error);
      alert('Error al actualizar datos');
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="edit-user-container">
      {loading && (
        <div className="flex items-center justify-center p-8 min-h-[300px]" data-testid="edit-user-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-700"></div>
          <span className="ml-3 text-gray-700">Cargando información del usuario...</span>
        </div>
      )}

      {!loading && !error && (
        <div className="flex flex-col gap-4" data-testid="edit-user-wrapper">
          <div className="flex justify-between items-center mb-2">
            <h1 className="text-xl font-bold text-purple-700" data-testid="edit-user-page-title">
              Editar datos del usuario
            </h1>
            <Link 
              to="/users-administration-list" 
              className="px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
              data-testid="edit-user-back-button"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
              <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
              <p className="z-0 text-gray-900">Volver a la lista</p>
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="edit-user-content">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-amber-100">
              <h2 className="text-xl font-bold text-gray-800" data-testid="edit-user-form-title">
                Información del usuario
              </h2>
              <p className="text-gray-600 text-sm mt-1" data-testid="edit-user-form-subtitle">
                Actualiza la información básica del usuario
              </p>
            </div>

            <form className="p-6" onSubmit={handleSubmit} data-testid="edit-user-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4" data-testid="edit-user-personal-info">
                  <div className="mb-4" data-testid="edit-user-name-field">
                    <label
                      className="block text-gray-700 text-sm font-bold mb-2 text-start"
                      htmlFor="name"
                    >
                      Nombres
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Nombres"
                      value={userData.name}
                      onChange={handleChange}              
                      required
                      data-testid="edit-user-name-input"
                    />
                  </div>
                  
                  <div className="mb-4" data-testid="edit-user-lastname-field">
                    <label
                      className="block text-gray-700 text-sm font-bold mb-2 text-start"
                      htmlFor="last_name"
                    >
                      Apellidos
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      type="text"
                      id="last_name"
                      name="last_name"
                      placeholder="Apellidos"
                      value={userData.last_name}
                      onChange={handleChange}
                      required
                      data-testid="edit-user-lastname-input"
                    />
                  </div>
                  
                  <div className="mb-4" data-testid="edit-user-document-field">
                    <label
                      className="block text-gray-700 text-sm font-bold mb-2 text-start"
                      htmlFor="document"
                    >
                     Número de documento
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed"
                      type="text"
                      id="document"
                      name="document"
                      value={userData.document}
                      disabled
                      data-testid="edit-user-document-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      El número de documento no puede ser modificado
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4" data-testid="edit-user-additional-info">
                  {rollByParams === 2 && (
                    <>
                      <div className="mb-4" data-testid="edit-user-email-field">
                        <label
                          className="block text-gray-700 text-sm font-bold mb-2 text-start"
                          htmlFor="email"
                        >
                          Correo electrónico
                        </label>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Correo electrónico"
                          value={userData.email}
                          onChange={handleChange}
                          data-testid="edit-user-email-input"
                        />
                      </div>
                      
                      <div className="mb-4" data-testid="edit-user-ficha-field">
                        <label
                          className="block text-gray-700 text-sm font-bold mb-2 text-start"
                          htmlFor="ficha"
                        >
                          Ficha
                        </label>
                        <input
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                          type="text"
                          id="ficha"
                          name="ficha"
                          placeholder="Número de ficha"
                          value={userData.ficha}
                          onChange={handleChange}
                          data-testid="edit-user-ficha-input"
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-4" data-testid="edit-user-actions-container">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3" data-testid="edit-user-actions-title">
                      Acciones
                    </h3>
                    
                    <div className="flex flex-col gap-3">
                      <button
                        type="submit"
                        className="w-full px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                        data-testid="edit-user-submit-button"
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                        <span className="z-10">Guardar cambios</span>
                      </button>
                      
                      {/*<button*/}
                      {/*  type="button"*/}
                      {/*  onClick={handlerDelete}*/}
                      {/*  className="w-full px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group bg-red-600 hover:bg-red-700"*/}
                      {/*  data-testid="edit-user-delete-button"*/}
                      {/*>*/}
                      {/*  <span className="z-10">Eliminar usuario</span>*/}
                      {/*</button>*/}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="bg-white rounded-lg shadow-md p-6 text-center" data-testid="edit-user-error">
          <div className="text-amber-600 text-5xl mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 9 9 11-18 0 9 9 9 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">No se pudo cargar la información del usuario</h2>
          <p className="text-gray-600 mb-6">Lo sentimos, pero no hemos podido encontrar la información solicitada.</p>
          <Link 
            to="/users-administration-list" 
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md inline-flex items-center justify-center relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
            <span className="z-10 text-gray-900">Volver a la lista de usuarios</span>
          </Link>
        </div>
      )}

      {isSuccessModalOpen && (
        <SuccessModal
          message={successMessage}
          onClose={() => {
            setIsSuccessModalOpen(false);
            navigate(`/user-detail/id/${userData.id}?userType=${roleName}`);
          }}
         isOpen/>
      )}
    </div>
  );
}

export { EditUser };
